/**
 * UrbanTabbedPanel - Panel izquierdo con pestañas para módulo urbano
 *
 * @version 1.3 — Census 2022: nueva pestaña de resumen censal
 * @module core/modules/urban
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LandPlot, Building2, Grid3x3, MapPinned, FileText, FolderKanban,
  Eye, EyeOff, RotateCcw, DatabaseZap, Search, ChevronDown, ChevronUp, X,
  SlidersHorizontal,
} from 'lucide-react';
import { TablePagination } from '../../layouts/panels/LeftPanel/TablePagination';
import { Census2022SummaryCard } from '../../census-2022/Census2022SummaryCard';
import type { Census2022Status, SliceRow2022 } from '../../census-2022/census-2022-api';
import { census2022Api } from '../../census-2022/census-2022-api';
import {
  useCensusRadioLayerStore,
  type CensusThematicVariable,
} from '../../census-2022/census-radio-layer-store';
import { COLOR_RAMPS, DEFAULT_SYNTAX_RAMP } from '../../spatial/config/syntax-color-ramps';
import { BaseInventoryTab } from '../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../map-tools';
import { useUrbanElementColorsStore } from './store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from './constants/element-colors';
import type { UrbanElementId } from './constants/element-colors';
import { useBuildingsLayerStore } from '../../buildings/buildings-layer-store';
import type { BuildingFilters } from '../../buildings/buildings-layer-store';
import { BuildingStatsCard } from '../../buildings/BuildingStatsCard';
import { BuildingResidentialCriteriaPanel } from '../../buildings/BuildingResidentialCriteriaPanel';
import { useProject } from '../../projects';
import '../../layouts/panels/LeftPanel/tabbed-left-panel.css';

type UrbanTabType =
  | 'parcels'
  | 'buildings'
  | 'zoning'
  | 'land-use'
  | 'regulations'
  | 'projects'
  | 'census-2022';

interface TabInfo {
  id: UrbanTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  { id: 'parcels',     icon: <LandPlot size={16} />,     tooltip: 'Parcelas',          title: 'Parcelas',       emptyMessage: 'Sin parcelas registradas' },
  { id: 'buildings',   icon: <Building2 size={16} />,    tooltip: 'Edificios',         title: 'Edificios',      emptyMessage: 'Sin edificios registrados' },
  { id: 'zoning',      icon: <Grid3x3 size={16} />,      tooltip: 'Zonificación',      title: 'Zonificación',   emptyMessage: 'Sin zonas definidas' },
  { id: 'land-use',    icon: <MapPinned size={16} />,    tooltip: 'Usos de suelo',     title: 'Usos de suelo',  emptyMessage: 'Sin usos registrados' },
  { id: 'regulations', icon: <FileText size={16} />,     tooltip: 'Normativa',         title: 'Normativa',      emptyMessage: 'Sin normativa cargada' },
  { id: 'projects',    icon: <FolderKanban size={16} />, tooltip: 'Proyectos urbanos', title: 'Proyectos',      emptyMessage: 'Sin proyectos registrados' },
  { id: 'census-2022', icon: <DatabaseZap size={16} />,  tooltip: 'Censo 2022',        title: 'Censo 2022',     emptyMessage: '' },
];

// ─── Inventario edilicio V1.5.1 ──────────────────────────────────────────────

const PAGE_SIZE = 25;

const FUNCTIONAL_CLASS_SHORT: Record<string, string> = {
  residential:     'Resid.',
  mixed:           'Mixto',
  non_residential: 'No resid.',
};

function formatAreaShort(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)} k`;
  return `${Math.round(n)}`;
}

function matchesFilters(p: Record<string, unknown>, f: BuildingFilters, search: string): boolean {
  if (search) {
    const code = String(p['displayCode'] ?? '').toLowerCase();
    if (!code.includes(search.toLowerCase())) return false;
  }
  if (f.qualityStatus !== 'all' && p['qualityStatus'] !== f.qualityStatus) return false;
  if (f.requiresReview === 'only' && p['requiresReview'] !== true) return false;
  if (f.hasFootprintGeometry === 'with'    && p['hasFootprintGeometry'] !== true)  return false;
  if (f.hasFootprintGeometry === 'without' && p['hasFootprintGeometry'] === true)  return false;
  if (f.morphologyClass !== 'all' && p['morphologyClass'] !== f.morphologyClass) return false;
  if (f.builtMassClass === 'none' && p['builtMassClass'] != null) return false;
  if (f.builtMassClass !== 'all' && f.builtMassClass !== 'none' && p['builtMassClass'] !== f.builtMassClass) return false;
  if (f.functionalClass === 'unclassified' && p['functionalClass'] != null) return false;
  if (f.functionalClass !== 'all' && f.functionalClass !== 'unclassified' && p['functionalClass'] !== f.functionalClass) return false;
  if (f.reviewStatus === 'reviewed'   && p['isUserReviewed']   !== true)  return false;
  if (f.reviewStatus === 'unreviewed' && p['isUserReviewed']   === true)  return false;
  if (f.manualEditStatus === 'edited'   && p['hasManualOverride'] !== true) return false;
  if (f.manualEditStatus === 'unedited' && p['hasManualOverride'] === true) return false;
  if (f.effectivePopulationStatus !== 'all' && p['effectivePopulationStatus'] !== f.effectivePopulationStatus) return false;
  return true;
}

type SortKey = 'code' | 'area' | 'func' | 'rev';
type SortDir = 'asc' | 'desc';

interface BuildingInventoryListProps {
  projectId: string;
  searchCode: string;
}

function BuildingInventoryList({ projectId: _projectId, searchCode }: BuildingInventoryListProps): JSX.Element {
  const viewportFeatures   = useBuildingsLayerStore((s) => s.viewportFeatures);
  const filters            = useBuildingsLayerStore((s) => s.filters);
  const openBuildingReview = useBuildingsLayerStore((s) => s.openBuildingReview);
  const selectedBuilding   = useBuildingsLayerStore((s) => s.selectedBuildingForReview);

  const [page,    setPage]    = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('code');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filtered = useMemo(
    () => viewportFeatures.filter((f) => matchesFilters(f.properties ?? {}, filters, searchCode)),
    [viewportFeatures, filters, searchCode],
  );

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const pa = a.properties ?? {};
      const pb = b.properties ?? {};
      let cmp = 0;
      if (sortKey === 'code') {
        const ca = String(pa['displayCode'] ?? pa['id'] ?? '');
        const cb = String(pb['displayCode'] ?? pb['id'] ?? '');
        cmp = ca.localeCompare(cb, 'es-AR');
      } else if (sortKey === 'area') {
        cmp = Number(pa['footprintAreaM2'] ?? 0) - Number(pb['footprintAreaM2'] ?? 0);
      } else if (sortKey === 'func') {
        const fa = String((pa['effectiveFunctionalClass'] as string | null) ?? (pa['functionalClass'] as string | null) ?? '');
        const fb = String((pb['effectiveFunctionalClass'] as string | null) ?? (pb['functionalClass'] as string | null) ?? '');
        cmp = fa.localeCompare(fb, 'es-AR');
      } else if (sortKey === 'rev') {
        cmp = (pa['isUserReviewed'] === true ? 1 : 0) - (pb['isUserReviewed'] === true ? 1 : 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  // Reset page when result set changes
  useEffect(() => { setPage(1); }, [filtered.length]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows   = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSortClick = (sk: SortKey): void => {
    if (sortKey === sk) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(sk); setSortDir('asc'); }
  };

  const ThSort = ({ label, sk, cls }: { label: string; sk: SortKey; cls: string }): JSX.Element => {
    const isActive = sortKey === sk;
    return (
      <button
        type="button"
        className={`bld-list-th ${cls}${isActive ? ' is-sort-active' : ''}`}
        onClick={() => handleSortClick(sk)}
        title={`Ordenar por ${label}`}
      >
        {label}
        {isActive && (sortDir === 'asc' ? <ChevronUp size={8} /> : <ChevronDown size={8} />)}
      </button>
    );
  };

  if (viewportFeatures.length === 0) {
    return (
      <div className="bld-list-empty">
        Acercate al mapa (zoom ≥ 14) para ver edificios individuales
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="bld-list-empty">
        Ningún edificio coincide con los filtros activos
      </div>
    );
  }

  return (
    <div className="bld-list-section">
      {/* Header: título + contador */}
      <div className="bld-list-header">
        <span className="bld-list-title">Inventario</span>
        <span className="bld-list-count">
          {sorted.length.toLocaleString('es-AR')} en vista
        </span>
      </div>

      {/* Cabecera de columnas — ordenable */}
      <div className="bld-list-thead">
        <ThSort label="Código"   sk="code" cls="bld-list-th--code" />
        <ThSort label="Sup. m²"  sk="area" cls="bld-list-th--area" />
        <ThSort label="Función"  sk="func" cls="bld-list-th--class" />
        <ThSort label="Rev."     sk="rev"  cls="bld-list-th--rev" />
      </div>

      {/* Filas */}
      <div className="bld-list-tbody">
        {pageRows.map((feature) => {
          const p          = feature.properties ?? {};
          const id         = String(p['id'] ?? '');
          const code       = p['displayCode'] ? String(p['displayCode']) : id.substring(0, 8).toUpperCase();
          const area       = formatAreaShort(p['footprintAreaM2']);
          const funcKey    = (p['effectiveFunctionalClass'] as string | null) ?? (p['functionalClass'] as string | null);
          const funcLabel  = funcKey ? (FUNCTIONAL_CLASS_SHORT[funcKey] ?? funcKey) : '—';
          const reviewed   = p['isUserReviewed'] === true;
          const isSelected = selectedBuilding?.buildingId === id;

          const geo    = feature.geometry as GeoJSON.Point | undefined;
          const coords = geo?.type === 'Point'
            ? [geo.coordinates[0], geo.coordinates[1]] as [number, number]
            : null;

          const handleClick = (): void => {
            if (!id) return;
            openBuildingReview(id, p as Record<string, unknown>);
            if (coords) {
              window.dispatchEvent(
                new CustomEvent('building-zoom', { detail: { coordinates: coords } }),
              );
            }
          };

          return (
            <button
              key={id || code}
              type="button"
              className={`bld-list-row${isSelected ? ' is-selected' : ''}`}
              onClick={handleClick}
              title={`Revisar ${code}`}
            >
              <span className="bld-list-code">{code}</span>
              <span className="bld-list-area">{area}</span>
              <span className="bld-list-class">{funcLabel}</span>
              <span className="bld-list-rev">{reviewed ? 'Sí' : '—'}</span>
            </button>
          );
        })}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <TablePagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          mode="compact"
        />
      )}
    </div>
  );
}

// ─── Panel edilicio V1.5.2 ────────────────────────────────────────────────────

function BuildingsV1Panel({ projectId }: { projectId: string }): JSX.Element {
  const [searchCode,  setSearchCode]  = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const buildingsVisible = useBuildingsLayerStore((s) => s.visible);
  const toggleBuildings  = useBuildingsLayerStore((s) => s.toggle);
  const filters          = useBuildingsLayerStore((s) => s.filters);
  const setFilters       = useBuildingsLayerStore((s) => s.setFilters);
  const resetFilters     = useBuildingsLayerStore((s) => s.resetFilters);
  const stats            = useBuildingsLayerStore((s) => s.viewportStats);

  const activeFilterCount = [
    filters.functionalClass             !== 'all',
    filters.reviewStatus                !== 'all',
    filters.qualityStatus               !== 'all',
    filters.requiresReview              !== 'all',
    filters.hasFootprintGeometry        !== 'all',
    filters.morphologyClass             !== 'all',
    filters.builtMassClass              !== 'all',
    filters.manualEditStatus            !== 'all',
    filters.effectivePopulationStatus   !== 'all',
  ].filter(Boolean).length;

  const filterRow = <K extends keyof BuildingFilters>(
    label: string,
    key: K,
    options: { value: BuildingFilters[K]; label: string }[],
  ): JSX.Element => (
    <div className="bld-filter-row">
      <span className="bld-filter-label">{label}</span>
      <select
        className="bld-filter-select"
        value={filters[key] as string}
        onChange={(e) => setFilters({ [key]: e.target.value } as Partial<BuildingFilters>)}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <>
      {/* 1. Toggle de visibilidad */}
      <div className="urban-layer-toggle">
        <button
          type="button"
          className={`urban-layer-toggle-btn${buildingsVisible ? ' is-on' : ' is-off'}`}
          onClick={toggleBuildings}
          title={buildingsVisible ? 'Ocultar edificios en el mapa' : 'Mostrar edificios en el mapa'}
        >
          {buildingsVisible ? <Eye size={13} /> : <EyeOff size={13} />}
          <span>{buildingsVisible ? 'Edificios visibles' : 'Edificios ocultos'}</span>
        </button>
      </div>

      {/* 2. Resumen del proyecto */}
      <BuildingStatsCard projectId={projectId} />

      {/* 3. Criterios de inclusión residencial (V1.6) */}
      <BuildingResidentialCriteriaPanel projectId={projectId} />

      {/* 4. Buscador + botón de filtros en la misma fila */}
      <div className="bld-search-filter-row">
        <div className="bld-search-wrap">
          <Search size={11} className="bld-search-icon" />
          <input
            type="text"
            className="bld-search-input"
            placeholder="Buscar por código…"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
          />
          {searchCode && (
            <button
              type="button"
              className="bld-search-clear"
              onClick={() => setSearchCode('')}
              title="Limpiar búsqueda"
            >
              <X size={11} />
            </button>
          )}
        </div>
        <button
          type="button"
          className={`bld-filter-btn${filtersOpen ? ' is-open' : ''}${activeFilterCount > 0 ? ' has-active' : ''}`}
          onClick={() => setFiltersOpen((v) => !v)}
          title="Mostrar/ocultar filtros"
        >
          <SlidersHorizontal size={11} />
          <span>Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
          {filtersOpen ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
        </button>
      </div>

      {/* 4. Panel de filtros colapsable */}
      {filtersOpen && (
        <div className="bld-filter-bar">
          {activeFilterCount > 0 && (
            <div className="bld-filter-bar-reset-row">
              <button
                type="button"
                className="bld-filters-reset"
                onClick={() => resetFilters()}
                title="Limpiar todos los filtros"
              >
                <RotateCcw size={10} />
                <span>Limpiar todos</span>
              </button>
            </div>
          )}
          {filterRow('Función', 'functionalClass', [
            { value: 'all',             label: 'Todas' },
            { value: 'unclassified',    label: 'Sin clasificar' },
            { value: 'residential',     label: 'Residencial' },
            { value: 'mixed',           label: 'Mixto' },
            { value: 'non_residential', label: 'No residencial' },
          ])}
          {filterRow('Revisión', 'reviewStatus', [
            { value: 'all',        label: 'Todos' },
            { value: 'reviewed',   label: 'Revisados' },
            { value: 'unreviewed', label: 'No revisados' },
          ])}
          {filterRow('Calidad', 'qualityStatus', [
            { value: 'all',     label: 'Todas' },
            { value: 'high',    label: 'Alta' },
            { value: 'medium',  label: 'Media' },
            { value: 'low',     label: 'Baja' },
            { value: 'unknown', label: 'Desconocida' },
          ])}
          {filterRow('Revisión sistema', 'requiresReview', [
            { value: 'all',  label: 'Todos' },
            { value: 'only', label: 'Solo los que requieren' },
          ])}
          {filterRow('Morfología', 'morphologyClass', [
            { value: 'all',                          label: 'Todas' },
            { value: 'small_detached_tiny',           label: 'Pieza muy pequeña' },
            { value: 'small_detached',                label: 'Edificación pequeña' },
            { value: 'medium_detached',               label: 'Edificación media' },
            { value: 'possible_multifamily_or_mixed', label: 'Posible multifam./mixto' },
            { value: 'compact_high_rise',             label: 'Compacto en altura' },
            { value: 'large_single_floor',            label: 'Gran huella baja' },
            { value: 'medium_complex',                label: 'Complejo mediano' },
            { value: 'large_high_mass_complex',       label: 'Gran complejo' },
            { value: 'requires_use_review',           label: 'Requiere revisión' },
          ])}
          {filterRow('Masa construida', 'builtMassClass', [
            { value: 'all',       label: 'Todas' },
            { value: 'minimal',   label: 'Mínima <60 m²' },
            { value: 'low',       label: 'Baja 60–250 m²' },
            { value: 'medium',    label: 'Media 250–1k m²' },
            { value: 'high',      label: 'Alta 1–5k m²' },
            { value: 'very_high', label: 'Muy alta >5k m²' },
            { value: 'none',      label: 'Sin estimación' },
          ])}
          {filterRow('Huella', 'hasFootprintGeometry', [
            { value: 'all',     label: 'Todos' },
            { value: 'with',    label: 'Con huella' },
            { value: 'without', label: 'Sin huella' },
          ])}
          {filterRow('Edición manual', 'manualEditStatus', [
            { value: 'all',     label: 'Todos' },
            { value: 'edited',  label: 'Editados' },
            { value: 'unedited',label: 'Sin edición' },
          ])}
          {filterRow('Población', 'effectivePopulationStatus', [
            { value: 'all',             label: 'Todas' },
            { value: 'auto_eligible',   label: 'Auto elegible' },
            { value: 'auto_excluded',   label: 'Auto excluido' },
            { value: 'manual_included', label: 'Incluido manual' },
            { value: 'manual_excluded', label: 'Excluido manual' },
          ])}
        </div>
      )}

      {/* 5. Viewport stats compacto */}
      {stats.totalLoaded > 0 && (
        <div className="bld-vstat-bar">
          <span className="bld-vstat-bar-item">
            <span className="bld-vstat-bar-label">Vista</span>
            <span className="bld-vstat-bar-val">{stats.totalLoaded.toLocaleString('es-AR')}</span>
          </span>
          <span className="bld-vstat-bar-sep" />
          <span className="bld-vstat-bar-item bld-vstat-bar-warn">
            <span className="bld-vstat-bar-label">Rev. requerida</span>
            <span className="bld-vstat-bar-val">{stats.needsReview.toLocaleString('es-AR')}</span>
          </span>
          {stats.manualOverrides > 0 && (
            <>
              <span className="bld-vstat-bar-sep" />
              <span className="bld-vstat-bar-item bld-vstat-bar-blue">
                <span className="bld-vstat-bar-label">Editados</span>
                <span className="bld-vstat-bar-val">{stats.manualOverrides.toLocaleString('es-AR')}</span>
              </span>
            </>
          )}
        </div>
      )}

      {/* 6. Inventario tabular con paginación */}
      <BuildingInventoryList projectId={projectId} searchCode={searchCode} />
    </>
  );
}

// ─── Panel Censo 2022 ─────────────────────────────────────────────────────────

const CENSUS_PAGE_SIZE = 20;

type C22SortKey    = 'radioCode' | 'departmentName' | 'coverageRatio' | 'popTotalWeighted' | 'pctNbi';
type C22FilterMode = 'all' | 'full' | 'partial' | 'no-indicators';

function CensusRadioInventoryList({ projectId }: { projectId: string }): JSX.Element | null {
  // ── Todos los hooks primero — sin excepción ────────────────────────────────
  const [slices,      setSlices]      = useState<SliceRow2022[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(1);
  const [sortKey,     setSortKey]     = useState<C22SortKey>('coverageRatio');
  const [sortDir,     setSortDir]     = useState<SortDir>('desc');
  const [filterMode,  setFilterMode]  = useState<C22FilterMode>('all');
  const [search,      setSearch]      = useState('');

  const selectRadio  = useCensusRadioLayerStore((s) => s.selectRadio);
  const selectedCode = useCensusRadioLayerStore((s) => s.selectedRadio?.radioCode ?? null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    census2022Api.getSlices(projectId, { limit: 500 })
      .then((r) => setSlices(r.slices))
      .catch(() => setSlices([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleZoom = useCallback((radioCode: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('census-radio-zoom', { detail: { radioCode } }));
  }, []);

  const handleSelect = useCallback((slice: SliceRow2022): void => {
    selectRadio({
      radioCode:                 slice.radioCode,
      departmentName:            slice.departmentName,
      provinceName:              slice.provinceName,
      coverageRatio:             slice.coverageRatio,
      isFull:                    slice.isFull,
      hasIndicators:             slice.hasIndicators,
      populationTotalWeighted:   slice.popTotalWeighted,
      householdsTotalWeighted:   slice.householdsTotalWeighted,
      householdsWithNbiWeighted: slice.householdsWithNbiWeighted,
      radioAreaM2:               slice.radioAreaM2,
      intersectionAreaM2:        slice.intersectionAreaM2,
    });
  }, [selectRadio]);

  // Derived state — before early returns
  const filtered = useMemo(() => {
    return slices.filter((sl) => {
      if (filterMode === 'full'         && !sl.isFull)          return false;
      if (filterMode === 'partial'      && sl.isFull)           return false;
      if (filterMode === 'no-indicators' && sl.hasIndicators)   return false;
      if (search) {
        const q    = search.toLowerCase();
        const code = sl.radioCode.toLowerCase();
        const dept = (sl.departmentName ?? '').toLowerCase();
        if (!code.includes(q) && !dept.includes(q)) return false;
      }
      return true;
    });
  }, [slices, filterMode, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'radioCode') {
        cmp = a.radioCode.localeCompare(b.radioCode, 'es-AR');
      } else if (sortKey === 'departmentName') {
        cmp = (a.departmentName ?? '').localeCompare(b.departmentName ?? '', 'es-AR');
      } else if (sortKey === 'coverageRatio') {
        cmp = a.coverageRatio - b.coverageRatio;
      } else if (sortKey === 'popTotalWeighted') {
        cmp = (a.popTotalWeighted ?? 0) - (b.popTotalWeighted ?? 0);
      } else if (sortKey === 'pctNbi') {
        const pa = a.householdsTotalWeighted ? (a.householdsWithNbiWeighted ?? 0) / a.householdsTotalWeighted : 0;
        const pb = b.householdsTotalWeighted ? (b.householdsWithNbiWeighted ?? 0) / b.householdsTotalWeighted : 0;
        cmp = pa - pb;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  // Reset page when filter/search result count changes
  useEffect(() => { setPage(1); }, [filtered.length]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / CENSUS_PAGE_SIZE));
  const pageSlices = useMemo(
    () => sorted.slice((page - 1) * CENSUS_PAGE_SIZE, page * CENSUS_PAGE_SIZE),
    [sorted, page],
  );

  // ── Early returns después de todos los hooks ───────────────────────────────
  if (loading) return <div className="c22-inv-loading">Cargando radios…</div>;
  if (slices.length === 0) return null;

  const handleSortClick = (sk: C22SortKey): void => {
    if (sortKey === sk) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(sk);
      // numeric columns default desc, text columns default asc
      setSortDir(sk === 'radioCode' || sk === 'departmentName' ? 'asc' : 'desc');
    }
  };

  const ThSort = ({ label, sk, cls }: { label: string; sk: C22SortKey; cls: string }): JSX.Element => {
    const isActive = sortKey === sk;
    return (
      <button
        type="button"
        className={`c22-tbl-th ${cls}${isActive ? ' is-sort-active' : ''}`}
        onClick={() => handleSortClick(sk)}
        title={`Ordenar por ${label}`}
      >
        {label}
        {isActive && (sortDir === 'asc' ? <ChevronUp size={8} /> : <ChevronDown size={8} />)}
      </button>
    );
  };

  const FILTER_CHIPS: { mode: C22FilterMode; label: string }[] = [
    { mode: 'all',           label: 'Todos' },
    { mode: 'full',          label: 'Completos' },
    { mode: 'partial',       label: 'Parciales' },
    { mode: 'no-indicators', label: 'Sin datos' },
  ];

  return (
    <div className="c22-inv-section">

      {/* Header */}
      <div className="c22-inv-header">
        <span className="c22-inv-title">Radios censales</span>
        <span className="c22-inv-count">{slices.length.toLocaleString('es-AR')}</span>
      </div>

      {/* Buscador */}
      <div className="c22-tbl-search-wrap">
        <Search size={10} className="c22-tbl-search-icon" />
        <input
          type="text"
          className="c22-tbl-search"
          placeholder="Buscar código o depto…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="c22-tbl-search-clear"
            onClick={() => setSearch('')}
            title="Limpiar búsqueda"
          >
            <X size={10} />
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="c22-tbl-chips">
        {FILTER_CHIPS.map(({ mode, label }) => (
          <button
            key={mode}
            type="button"
            className={`c22-tbl-chip${filterMode === mode ? ' is-active' : ''}`}
            onClick={() => setFilterMode(mode)}
          >
            {label}
          </button>
        ))}
        {filtered.length !== slices.length && (
          <span className="c22-tbl-chip-count">{filtered.length}</span>
        )}
      </div>

      {/* Cabecera de columnas ordenable */}
      <div className="c22-tbl-thead">
        <span className="c22-tbl-th c22-tbl-th--zoom" />
        <ThSort label="Radio"  sk="radioCode"        cls="c22-tbl-th--code" />
        <ThSort label="Depto"  sk="departmentName"   cls="c22-tbl-th--dept" />
        <span className="c22-tbl-th c22-tbl-th--tipo">T</span>
        <ThSort label="Cob%"   sk="coverageRatio"    cls="c22-tbl-th--cov" />
        <ThSort label="Pob"    sk="popTotalWeighted"  cls="c22-tbl-th--pop" />
        <ThSort label="NBI%"   sk="pctNbi"           cls="c22-tbl-th--nbi" />
      </div>

      {/* Filas */}
      <div className="c22-tbl-tbody">
        {filtered.length === 0 ? (
          <div className="c22-tbl-empty">Sin resultados para los filtros activos</div>
        ) : (
          pageSlices.map((slice) => {
            const isSelected = slice.radioCode === selectedCode;
            const cov = `${(slice.coverageRatio * 100).toFixed(1)}%`;
            const pop = slice.popTotalWeighted != null
              ? Math.round(slice.popTotalWeighted).toLocaleString('es-AR')
              : '—';
            const nbi = slice.householdsTotalWeighted
              ? `${((slice.householdsWithNbiWeighted ?? 0) / slice.householdsTotalWeighted * 100).toFixed(1)}%`
              : '—';
            const tipo = slice.isFull ? 'C' : 'P'; // Completo / Parcial

            return (
              <div
                key={slice.id}
                role="button"
                tabIndex={0}
                className={`c22-tbl-row${isSelected ? ' is-selected' : ''}${slice.isFull ? '' : ' is-partial'}`}
                onClick={() => handleSelect(slice)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(slice); }}
                title={`Radio ${slice.radioCode} — ${cov} dentro del proyecto`}
              >
                <button
                  type="button"
                  className="c22-tbl-zoom"
                  onClick={(e) => handleZoom(slice.radioCode, e)}
                  title="Acercar en el mapa"
                >
                  <Search size={9} />
                </button>
                <span className="c22-tbl-code">{slice.radioCode}</span>
                <span className="c22-tbl-dept" title={slice.departmentName ?? undefined}>
                  {slice.departmentName ?? '—'}
                </span>
                <span className={`c22-tbl-tipo${slice.isFull ? ' is-full' : ' is-partial'}`}>{tipo}</span>
                <span className={`c22-tbl-cov${slice.isFull ? ' is-full' : ' is-partial'}`}>{cov}</span>
                <span className="c22-tbl-pop">{pop}</span>
                <span className="c22-tbl-nbi">{nbi}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} mode="compact" />
      )}
    </div>
  );
}

// ─── Thematic variable selector ──────────────────────────────────────────────

const THEMATIC_LABELS: Record<CensusThematicVariable, string> = {
  population:     'Población total',
  pop_density_ha: 'Densidad hab./ha',
  households:     'Hogares totales',
  hh_density_ha:  'Densidad hogares/ha',
  nbi_pct:        '% Hogares con NBI',
};

function CensusThematicSelector(): JSX.Element {
  const thematicVariable  = useCensusRadioLayerStore((s) => s.thematicVariable);
  const thematicRanges    = useCensusRadioLayerStore((s) => s.thematicRanges);
  const setThematicVariable = useCensusRadioLayerStore((s) => s.setThematicVariable);
  const setVisible          = useCensusRadioLayerStore((s) => s.setVisible);

  const ramp = COLOR_RAMPS[DEFAULT_SYNTAX_RAMP];
  const gradientStops = ramp.stops.map((s) => `${s.color} ${s.stop * 100}%`).join(', ');

  const currentRange = thematicVariable ? thematicRanges[thematicVariable] : null;

  const formatValue = (v: number, variable: CensusThematicVariable): string => {
    const isPercent = variable === 'nbi_pct';
    if (isPercent) return `${v.toFixed(1)}%`;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    if (v >= 10)   return v.toFixed(0);
    return v.toFixed(1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const val = e.target.value as CensusThematicVariable | '';
    if (val === '') {
      setThematicVariable(null);
    } else {
      setThematicVariable(val);
      setVisible(true); // ensure layer is visible when activating thematic
    }
  };

  return (
    <div style={{
      padding: '10px 12px',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      background: 'rgba(0,0,0,0.15)',
    }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#777', marginBottom: 6 }}>
        Visualizar en mapa
      </div>

      {/* Select wrapper */}
      <div style={{ position: 'relative' }}>
        <select
          value={thematicVariable ?? ''}
          onChange={handleChange}
          style={{
            width: '100%',
            padding: '6px 28px 6px 10px',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 4,
            color: '#fff',
            fontSize: 12,
            cursor: 'pointer',
            appearance: 'none',
          }}
        >
          <option value="">— Sin visualización —</option>
          {(Object.entries(THEMATIC_LABELS) as [CensusThematicVariable, string][]).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <ChevronDown size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#888' }} />
      </div>

      {/* Gradient bar */}
      {thematicVariable && currentRange && (
        <div style={{ marginTop: 8 }}>
          <div style={{
            height: 10,
            borderRadius: 5,
            background: `linear-gradient(to right, ${gradientStops})`,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)',
          }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#999' }}>
              {formatValue(currentRange.min, thematicVariable)}
            </span>
            <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#999' }}>
              {formatValue(currentRange.p95, thematicVariable)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Panel de censo 2022 ──────────────────────────────────────────────────────

interface Census2022PanelProps {
  projectId: string;
}

function Census2022Panel({ projectId }: Census2022PanelProps): JSX.Element {
  const [censusStatus, setCensusStatus] = useState<Census2022Status>('none');
  const isReady = censusStatus === 'ready_2022' || censusStatus === 'stale_2022';

  return (
    <>
      {/* Resumen censal */}
      <Census2022SummaryCard projectId={projectId} onStatusChange={setCensusStatus} />

      {/* Selector de variable temática — solo cuando hay datos */}
      {isReady && <CensusThematicSelector />}

      {/* Inventario — solo cuando hay datos */}
      {isReady && <CensusRadioInventoryList projectId={projectId} />}
    </>
  );
}

// ─── Panel principal ──────────────────────────────────────────────────────────

export function UrbanTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<UrbanTabType>('parcels');
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useUrbanElementColorsStore((s) => s.getColor);
  const setColor = useUrbanElementColorsStore((s) => s.setColor);
  const { projectId } = useProject();
  const setBuildingsVisible = useBuildingsLayerStore((s) => s.setVisible);

  // Edificios solo visibles cuando la pestaña activa es Edificios
  useEffect(() => {
    setBuildingsVisible(activeTab === 'buildings');
  }, [activeTab, setBuildingsVisible]);

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `urban-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  const handleCodeClick = async (elementId: string): Promise<void> => {
    const canChange = await canChangeContent();
    if (!canChange) return;
    selectElement(selectionKey, elementId);
    window.dispatchEvent(new CustomEvent(`urban-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            {activeTab === 'buildings' ? (
              <BuildingsV1Panel projectId={projectId ?? ''} />
            ) : activeTab === 'census-2022' ? (
              <Census2022Panel projectId={projectId ?? ''} />
            ) : (
              <BaseInventoryTab
                key={activeTab}
                title={activeTabInfo.title}
                emptyMessage={activeTabInfo.emptyMessage}
                eventPrefix={`urban-${activeTab}`}
                moduleId="urban"
                elementColor={isMappable ? getColor(activeTab as UrbanElementId) : undefined}
                onElementColorChange={isMappable ? (hex) => setColor(activeTab as UrbanElementId, hex) : undefined}
                onCodeClick={handleCodeClick}
                activeElementId={activeElementId}
              />
            )}
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
